// ═══════════════════════════════════════════════════════════
// شاشة تغيير كلمة المرور — تظهر عند أول دخول
// ═══════════════════════════════════════════════════════════

import React, { useState } from "react";
import { Lock, Eye, EyeOff, CheckCircle, Shield } from "lucide-react";
import { C, ORG } from "./constants";
import { changePassword } from "./supabaseClient";

export default function ChangePasswordPage({ user, onComplete }) {
  const [current, setCurrent] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!current || !newPw || !confirm) { setError("يرجى ملء جميع الحقول"); return; }
    if (newPw.length < 10) { setError("كلمة المرور الجديدة يجب أن تكون 10 أحرف على الأقل"); return; }
    if (newPw !== confirm) { setError("كلمة المرور الجديدة غير متطابقة"); return; }
    if (newPw === current) { setError("كلمة المرور الجديدة يجب أن تكون مختلفة عن الحالية"); return; }

    setLoading(true);
    try {
      // السيرفر يتحقق من الكلمة المؤقتة ويلغي علم التغيير الإجباري (BUG-017)
      await changePassword(current, newPw);
      onComplete();
    } catch (err) {
      setError("حدث خطأ: " + err.message);
    }
    setLoading(false);
  };

  const inputStyle = {
    width: "100%", padding: "12px 40px", border: `1.5px solid ${C.border}`,
    borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none",
    background: "#fff", color: C.text, transition: "border-color 0.2s",
  };

  return (
    <div style={{
      direction: "rtl", fontFamily: "'Readex Pro', sans-serif",
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: `linear-gradient(160deg, #003d58 0%, #001a28 100%)`, padding: 20,
    }}>
      <div style={{
        background: "#fff", borderRadius: 20, padding: "36px 32px",
        width: "100%", maxWidth: 420, boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14, background: `${C.accent}15`,
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 14px",
          }}>
            <Shield size={28} style={{ color: C.accent }} />
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: C.primary, margin: "0 0 6px" }}>
            تغيير كلمة المرور
          </h2>
          <p style={{ fontSize: 12, color: C.textLight, margin: 0 }}>
            مرحباً {user.name}، يرجى تعيين كلمة مرور جديدة للمتابعة
          </p>
        </div>

        {error && (
          <div style={{
            padding: "10px 14px", background: `${C.danger}08`,
            borderRight: `3px solid ${C.danger}`, borderRadius: "0 8px 8px 0",
            marginBottom: 18, fontSize: 12, color: C.danger,
          }}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: C.textLight, display: "block", marginBottom: 6 }}>
              كلمة المرور الحالية (المؤقتة)
            </label>
            <div style={{ position: "relative" }}>
              <Lock size={16} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: C.textLight }} />
              <input type={showPw ? "text" : "password"} value={current}
                onChange={(e) => setCurrent(e.target.value)} placeholder="كلمة المرور المؤقتة" style={inputStyle} />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: C.textLight, display: "block", marginBottom: 6 }}>
              كلمة المرور الجديدة
            </label>
            <div style={{ position: "relative" }}>
              <Lock size={16} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: C.textLight }} />
              <input type={showPw ? "text" : "password"} value={newPw}
                onChange={(e) => setNewPw(e.target.value)} placeholder="10 أحرف على الأقل" style={inputStyle} />
              <button type="button" onClick={() => setShowPw(!showPw)} style={{
                position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                background: "none", border: "none", cursor: "pointer", color: C.textLight,
              }}>{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: C.textLight, display: "block", marginBottom: 6 }}>
              تأكيد كلمة المرور الجديدة
            </label>
            <div style={{ position: "relative" }}>
              <Lock size={16} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: C.textLight }} />
              <input type={showPw ? "text" : "password"} value={confirm}
                onChange={(e) => setConfirm(e.target.value)} placeholder="أعد كتابة كلمة المرور" style={inputStyle} />
              {confirm && confirm === newPw && (
                <CheckCircle size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.success }} />
              )}
            </div>
          </div>

          <button type="submit" disabled={loading} style={{
            width: "100%", padding: "14px", border: "none", borderRadius: 10,
            fontSize: 14, fontWeight: 700, fontFamily: "inherit",
            cursor: loading ? "not-allowed" : "pointer",
            color: "#003d58", background: "#dcc160",
            boxShadow: "0 4px 16px rgba(220,193,96,0.3)",
          }}>
            {loading ? "جاري التحديث..." : "تأكيد وتسجيل الدخول"}
          </button>
        </form>

        <div style={{ marginTop: 20, textAlign: "center", fontSize: 9, color: C.textLight }}>
          © {new Date().getFullYear()} {ORG.shortName}
        </div>
      </div>

      <style>{`
        * { box-sizing: border-box; }
        input::placeholder { color: #9aacb8; opacity: 0.6; }
        input:focus { border-color: #dcc160 !important; }
      `}</style>
    </div>
  );
}
